<template>
  <div :class="['chat-message', message.sender]">

    <div v-if="message.sender === 'assistant'" class="avatar-wrapper">
      <div class="tech-avatar-container">
        <img
            :src="require('@/assets/logo.png')"
            class="robot-img tech-logo-filter"
            alt="AI"
        />
      </div>
    </div>

    <div class="message-bubble">
      <div class="message-content">
        <span v-if="message.sender === 'user'">{{ displayContent }}</span><span v-else>{{ typedText }}<span v-if="isTyping" class="typing-cursor">_</span></span>
      </div>

      <div
          v-if="message.sender === 'assistant' && !isTyping"
          class="copy-btn-wrapper"
          @click="copyText"
          :title="$t('chat.actions.copy')"
      >
        <i class="el-icon-document-copy"></i>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ChatMessage',
  props: {
    message: {
      type: Object,
      required: true,
      validator: function (value) {
        return value && value.sender !== undefined && value.content !== undefined;
      }
    },
    shouldType: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      typedText: '',
      isTyping: false,
      typingInterval: null,
      fullText: ''
    };
  },
  computed: {
    displayContent() {
      const lang = this.$i18n.locale;
      let content = this.message.content;

      if (lang === 'zh' && this.message.contentZh) content = this.message.contentZh;
      else if (lang === 'en' && this.message.contentEn) content = this.message.contentEn;

      // [逻辑严谨性] 兜底处理：如果内容是 null/undefined，返回空字符串，防止报错
      return content || '';
    }
  },
  watch: {
    displayContent: {
      immediate: true,
      handler(newVal) {
        this.fullText = newVal || '';

        if (this.message.sender === 'user' || !this.shouldType) {
          this.typedText = this.fullText;
          this.isTyping = false;
        } else {
          this.startTypewriter();
        }
      }
    }
  },
  methods: {
    startTypewriter() {
      if (this.typingInterval) clearInterval(this.typingInterval);

      this.typedText = '';
      this.isTyping = true;
      let currentIndex = 0;
      const speed = 30;

      this.typingInterval = setInterval(() => {
        if (currentIndex >= this.fullText.length) {
          clearInterval(this.typingInterval);
          this.isTyping = false;
          this.typedText = this.fullText;
          return;
        }
        this.typedText += this.fullText[currentIndex];
        currentIndex++;
      }, speed);
    },
    copyText() {
      if (!this.fullText) return;

      // 注意：此 API 需要 HTTPS 环境
      navigator.clipboard.writeText(this.fullText).then(() => {
        this.$message({
          message: this.$t('chat.actions.copySuccess'),
          type: 'success',
          duration: 2000,
        });
      }).catch(err => {
        console.error('Copy failed: ', err);
        // 如果 HTTPS 限制导致失败，可以考虑在这里添加降级方案，但对于现代 Web App 通常建议上 HTTPS
        this.$message.error(this.$t('chat.actions.copyFailed'));
      });
    }
  },
  beforeDestroy() {
    if (this.typingInterval) clearInterval(this.typingInterval);
  }
};
</script>

<style scoped>
/* 保持您满意的样式不变 */
.chat-message {
  display: flex;
  margin-bottom: 25px;
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.avatar-wrapper {
  margin-right: 15px;
  flex-shrink: 0;
  display: flex;
  align-items: flex-start;
  padding-top: 5px;
}

.tech-avatar-container {
  width: 45px;
  height: 45px;
  border-radius: 50%;
  background: rgba(10, 20, 35, 0.8);
  border: 1px solid var(--tech-primary);
  box-shadow: 0 0 15px rgba(0, 153, 255, 0.3);
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  position: relative;
}

.robot-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 8px;
  display: block;
}

.tech-logo-filter {
  filter: brightness(0) invert(1) drop-shadow(0 0 2px rgba(0, 153, 255, 0.8));
}

.chat-message.user {
  justify-content: flex-end;
}

.chat-message.user .message-bubble {
  background: linear-gradient(135deg, var(--tech-secondary) 0%, var(--tech-primary) 100%);
  color: #fff;
  border-radius: 12px 12px 4px 12px;
  box-shadow: 0 0 15px rgba(0, 87, 255, 0.4);
  margin-left: 50px;
}

.chat-message.assistant {
  justify-content: flex-start;
}

.chat-message.assistant .message-bubble {
  background-color: rgba(10, 20, 35, 0.7);
  backdrop-filter: blur(5px);
  color: var(--tech-text-main);
  border: 1px solid rgba(0, 153, 255, 0.3);
  border-radius: 4px 12px 12px 12px;
  box-shadow: inset 0 0 10px rgba(0, 153, 255, 0.05),
  0 2px 10px rgba(0, 0, 0, 0.3);
  margin-right: 50px;
  min-width: 60px;
}

.message-bubble {
  max-width: 80%;
  padding: 12px 18px;
  position: relative;
  word-break: break-word;
  line-height: 1.6;
  font-size: 15px;
  letter-spacing: 0.3px;
  padding-bottom: 12px;
}

.copy-btn-wrapper {
  position: absolute;
  bottom: -20px;
  right: 0;
  cursor: pointer;
  color: var(--tech-text-sub);
  opacity: 0;
  transition: all 0.3s ease;
  padding: 5px;
  display: flex;
  align-items: center;
}

.copy-btn-wrapper i {
  font-size: 16px;
}

.message-bubble:hover .copy-btn-wrapper {
  opacity: 1;
  bottom: 5px;
  right: 10px;
}

@media (max-width: 768px) {
  .copy-btn-wrapper {
    opacity: 0.6;
    bottom: 5px;
    right: 10px;
  }
  .tech-avatar-container { width: 36px; height: 36px; }
  .robot-img { padding: 6px; }
  .message-bubble { max-width: 85%; }
}

.copy-btn-wrapper:hover {
  color: var(--tech-primary);
  text-shadow: 0 0 5px var(--tech-primary);
}

.message-content {
  white-space: pre-wrap;
  font-family: 'Segoe UI', 'Roboto', sans-serif;
}

.typing-cursor {
  display: inline-block;
  width: 8px;
  height: 16px;
  background-color: var(--tech-primary);
  margin-left: 4px;
  vertical-align: text-bottom;
  animation: blink 1s step-end infinite;
}

@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}
</style>